import { type Theme } from '@mui/material/styles';
import { createBrandTheme, ruttiaBrand } from '@routes-plan/ui';
import { BRAND } from './generated/brand';

/** Fuentes de Ruttia (ruttia-platform/design/fuentes): títulos en Bricolage Grotesque, texto en Public Sans. */
const SYSTEM_FONTS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const BODY_FONT = `"Public Sans", ${SYSTEM_FONTS}`;
export const HEADING_FONT = `"Bricolage Grotesque", ${BODY_FONT}`;
const isHex = (value?: string) => /^#[0-9a-f]{6}$/i.test(value || '');

export type ColorMode = 'light' | 'dark';

/** Barras de desplazamiento de Ruttia en cada modo (página, tablas y áreas con scroll). */
export const scrollbarColors = (mode: ColorMode) => (mode === 'dark' ? { thumb: '#5B6B82', track: '#1B2533' } : { thumb: '#01274d', track: '#d9e1ea' });

/** Encabezado gris de tablas de procesos y reportes; opaco por el encabezado fijo. */
export const tableHeaderGrey = (mode: ColorMode) => (mode === 'dark' ? '#2A3546' : '#E4E8EE');

/** Encabezado de las tablas (durazno de Ruttia); en oscuro, el mismo tono mezclado con la superficie. Opaco por el encabezado fijo. */
export const tableHeaderBackground = (mode: ColorMode) => (mode === 'dark' ? '#574540' : '#FFAA77');

/** Colores de la marca de la empresa (brand.json); sin ellos, los de Ruttia. */
export const BRAND_PRIMARY = isHex(BRAND.web?.primaryColor) ? BRAND.web.primaryColor : ruttiaBrand.primary;
export const BRAND_SECONDARY = isHex(BRAND.web?.secondaryColor) ? BRAND.web.secondaryColor : ruttiaBrand.secondary;

/**
 * Tema de la web en modo claro u oscuro. Mismos tonos que @routes-plan/ui (panel y portal):
 * las pantallas solo deben usar colores del tema (`primary.main`, `background.paper`, `divider`,
 * `text.secondary`, `action.hover`…), nunca colores fijos, para que funcionen los dos modos.
 */
export const createAppTheme = (mode: ColorMode, company?: { name?: string; primary: string; secondary: string; logoUrl?: string | null } | null): Theme => {
  const { thumb: scrollThumb, track: scrollTrack } = scrollbarColors(mode);
  // Mismo tema que el panel y el portal (@routes-plan/ui), con los colores de la empresa en uso
  // (portal → Marca); sin ellos, los de la compilación o los de Ruttia.
  return createBrandTheme(
    {
      ...ruttiaBrand,
      name: company?.name || BRAND.displayName || ruttiaBrand.name,
      ...(company?.logoUrl ? { logo: { light: company.logoUrl } } : BRAND.web?.assetUrls?.logoFull ? { logo: { light: BRAND.web.assetUrls.logoFull } } : {}),
      primary: isHex(company?.primary) ? company!.primary : BRAND_PRIMARY,
      secondary: isHex(company?.secondary) ? company!.secondary : BRAND_SECONDARY,
      fonts: { body: '"Public Sans"', heading: '"Bricolage Grotesque"' },
    },
    mode,
    {
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            html: { scrollbarColor: `${scrollThumb} ${scrollTrack}` },
            body: { scrollbarColor: `${scrollThumb} ${scrollTrack}` },
          },
        },
      },
    },
  );
};

/** Barra superior: azul marino de Ruttia en claro; en oscuro, la superficie del tema. */
export const appBarBackground = (theme: Theme) => (theme.palette.mode === 'dark' ? theme.palette.background.paper : '#01274d');

/** Tema claro, para código que todavía lo importa directo (preferir useTheme()). */
const theme = createAppTheme('light');
export default theme;
